import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { Subscription, interval, timer } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AuthService } from '../../../services/auth/auth.service';
import {
  SupportAttachmentMeta,
  SupportChatMessage,
  SupportConversationStatus,
  SupportConversationSummary,
  SupportMessagesService,
} from '../../../services/support-messages/support-messages.service';
import {
  SupportTemplateCategory,
  SupportTemplateItem,
  SupportTemplatesService,
  prepareHelpBotCategories,
} from '../../../services/support-templates/support-templates.service';
import {
  HelpBotFaqMatchedItem,
  matchHelpBotFaq,
} from './help-bot-faq-match.util';
import {
  COMPOSER_EMOJI_SET,
  insertTextAtCursor,
} from './composer-emoji.util';
import {
  ComposerSpeechController,
  isSpeechInputSupported,
  prepareSpeechTranscriptForInsert,
} from './composer-speech.util';
import {
  ComposerVoiceNoteRecorder,
  VOICE_NOTE_MAX_SECONDS,
  formatVoiceNoteSeconds,
  isVoiceNoteRecordingSupported,
} from './composer-voice-note.util';

type HelpBotView =
  | 'categories'
  | 'searches'
  | 'questions'
  | 'answer'
  | 'thread'
  | 'history';

const THREAD_POLL_MS = 5000;
const NUDGE_DELAY_MS = 4000;
const NUDGE_VISIBLE_MS = 3000;
const NUDGE_REPEAT_MS = 20000;
/**
 * Home topic chips (search products stay under Available searches).
 * Right-aligned / longest-first packing is handled in CSS + suggestionChips.
 */
const HOME_CHIP_LIMIT = 8;
const SEEN_ADMIN_STORAGE_KEY = 'help-bot-seen-admin';
const WAITING_SINCE_STORAGE_KEY = 'help-bot-waiting-since';

/** Product search topics grouped under one home chip (API already permission-filters). */
const SEARCH_TOPIC_KEYS = new Set([
  'osint_search',
  'basic_search',
  'face_search',
  'leak_search',
  'vehicle',
  'pan',
  'gst_to_mobile',
  'din_to_mobile',
  'mobile_to_pan',
  'fastag',
  'upi_to_bank',
  'advanced_search',
  'web_capture',
  'virtual_number',
  'fastrak',
  'link_analysis',
]);

const SEARCHES_CHIP_LABEL = 'Available searches';

/** Strip signature query so poll-rotated presigned URLs stay identity-stable. */
function attachmentPath(url: string | null | undefined): string {
  return (url || '').split('?')[0];
}

function attachmentStableId(message: SupportChatMessage): string {
  const size =
    message.attachment_size == null ? '' : String(message.attachment_size);
  return [
    message.attachment_name || '',
    message.attachment_mime || '',
    size,
    attachmentPath(message.attachment_url),
  ].join('\0');
}

function supportMessageKey(message: SupportChatMessage): string {
  return `${message.sender}\0${message.created_on}\0${message.body}\0${attachmentStableId(message)}`;
}

type HelpBotThreadRow =
  | { kind: 'date'; id: string; label: string }
  | { kind: 'message'; id: string; message: SupportChatMessage };

function threadDayKey(iso: string | null | undefined): string {
  const time = Date.parse(iso || '');
  if (Number.isNaN(time)) return '';
  const date = new Date(time);
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

/** Crisp-style: "Wednesday, 4 November" */
function formatThreadDateLabel(iso: string | null | undefined): string {
  const time = Date.parse(iso || '');
  if (Number.isNaN(time)) return '';
  try {
    return new Date(time).toLocaleDateString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  } catch {
    return '';
  }
}

function buildThreadRows(messages: SupportChatMessage[]): HelpBotThreadRow[] {
  const rows: HelpBotThreadRow[] = [];
  let lastDay = '';
  for (const message of messages) {
    const day = threadDayKey(message.created_on);
    if (day && day !== lastDay) {
      lastDay = day;
      const label = formatThreadDateLabel(message.created_on);
      if (label) {
        rows.push({ kind: 'date', id: `date-${day}`, label });
      }
    }
    rows.push({
      kind: 'message',
      id: supportMessageKey(message),
      message,
    });
  }
  return rows;
}

/** Keep prior signed URLs so <img>/<audio> do not reload on every poll. */
function reuseAttachmentUrls(
  previous: SupportChatMessage[],
  incoming: SupportChatMessage[],
): SupportChatMessage[] {
  if (!previous.length) return incoming;
  const prevByKey = new Map(
    previous.map((message) => [supportMessageKey(message), message]),
  );
  return incoming.map((message) => {
    const prev = prevByKey.get(supportMessageKey(message));
    if (
      !prev?.attachment_url ||
      !message.attachment_url ||
      attachmentPath(prev.attachment_url) !==
        attachmentPath(message.attachment_url)
    ) {
      return message;
    }
    if (prev.attachment_url === message.attachment_url) return message;
    return { ...message, attachment_url: prev.attachment_url };
  });
}

const SUPPORT_ATTACH_ACCEPT =
  'image/jpeg,image/png,image/webp,image/gif,application/pdf,audio/webm,audio/ogg,audio/mpeg,audio/mp4,audio/wav,.webm,.ogg,.mp3,.m4a,.wav';
const SUPPORT_ATTACH_MAX_BYTES = 5 * 1024 * 1024;
const SUPPORT_ATTACH_ALLOWED_PREFIXES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  'audio/webm',
  'audio/ogg',
  'audio/mpeg',
  'audio/mp4',
  'audio/mp3',
  'audio/wav',
] as const;

function isAllowedSupportAttachMime(mime: string): boolean {
  const normalized = (mime || '').toLowerCase().split(';')[0].trim();
  if (!normalized) return false;
  return SUPPORT_ATTACH_ALLOWED_PREFIXES.some(
    (allowed) =>
      normalized === allowed || normalized.startsWith(`${allowed};`),
  );
}

const PUBLIC_PATHS = new Set([
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/otp',
  '/privacy-policy',
  '/login/verify-2fa',
]);

@Component({
  selector: 'app-help-bot-widget',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './help-bot-widget.component.html',
  styleUrl: './help-bot-widget.component.css',
})
export class HelpBotWidgetComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  @ViewChild('panelBody') panelBody?: ElementRef<HTMLElement>;
  @ViewChild('composerInput') composerInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('attachInput') attachInput?: ElementRef<HTMLInputElement>;

  visible = false;
  open = false;
  loading = false;
  error = '';
  view: HelpBotView = 'categories';
  categories: SupportTemplateCategory[] = [];
  selectedCategory: SupportTemplateCategory | null = null;
  selectedItem: SupportTemplateItem | null = null;
  messages: SupportChatMessage[] = [];
  draft = '';
  /** Pending uploaded attachment for the open thread composer. */
  pendingAttachment: SupportAttachmentMeta | null = null;
  /**
   * Local file chosen on FAQ/home before a conversation exists.
   * Uploaded after the admin thread opens on send.
   */
  pendingHomeAttachFile: File | null = null;
  attachUploading = false;
  attachError = '';
  readonly attachAccept = SUPPORT_ATTACH_ACCEPT;
  /** Compact emoji picker open state (closes on select / outside click / Escape). */
  emojiPickerOpen = false;
  readonly emojiSet = COMPOSER_EMOJI_SET;
  /** Browser Web Speech API available for voice-to-text. */
  speechSupported = isSpeechInputSupported();
  speechListening = false;
  /** Listening / interim / soft error status for dictation. */
  speechStatus = '';
  /** Browser MediaRecorder available for voice notes. */
  voiceNoteSupported = isVoiceNoteRecordingSupported();
  voiceNoteRecording = false;
  voiceNoteSeconds = 0;
  /** Recording / soft error status for voice notes. */
  voiceNoteStatus = '';
  threadLoading = false;
  threadError = '';
  sending = false;
  sendError = '';
  unreadCount = 0;
  nudgeVisible = false;
  conversationId: string | null = null;
  conversationStatus: SupportConversationStatus | null = null;
  historyLoading = false;
  historyError = '';
  historyItems: SupportConversationSummary[] = [];
  menuOpen = false;
  /** Larger Crisp-style panel from Quick Actions → Expand view. */
  panelExpanded = false;
  closingChat = false;
  startingChat = false;
  /** FAQ question chips from an ambiguous home-send match (overrides live category chips). */
  matchedQuestionChips: HelpBotFaqMatchedItem[] = [];
  /** Category chips from an ambiguous home-send match (overrides live title filter). */
  matchedCategoryChips: SupportTemplateCategory[] = [];

  private navSub?: Subscription;
  private templatesSub?: Subscription;
  private messagesSub?: Subscription;
  private pollSub?: Subscription;
  private sendSub?: Subscription;
  private uploadSub?: Subscription;
  private watchSub?: Subscription;
  private watchMessagesSub?: Subscription;
  private nudgeSub?: Subscription;
  private nudgeHideSub?: Subscription;
  private conversationsSub?: Subscription;
  private startChatSub?: Subscription;
  private closeChatSub?: Subscription;
  private historySub?: Subscription;
  private seenAdminKeys = new Set<string>();
  private waitingSince = '';
  private acknowledgeReplies = false;
  private pendingLocal: SupportChatMessage[] = [];
  private shouldScrollThread = false;
  private historyReturnView: HelpBotView = 'categories';
  /** Message typed on home; sent once an admin thread is open after FAQ miss. */
  private pendingHomeSend: string | null = null;
  private readonly speech: ComposerSpeechController;
  private readonly voiceNote: ComposerVoiceNoteRecorder;

  constructor(
    private authService: AuthService,
    private router: Router,
    private supportTemplates: SupportTemplatesService,
    private supportMessages: SupportMessagesService,
    private host: ElementRef<HTMLElement>,
    private ngZone: NgZone,
  ) {
    this.speech = new ComposerSpeechController({
      onListeningChange: (listening) => {
        this.ngZone.run(() => {
          this.speechListening = listening;
          if (listening) {
            this.speechStatus = 'Listening…';
          } else if (this.speechStatus.startsWith('Listening')) {
            this.speechStatus = '';
          }
        });
      },
      onInterim: (transcript) => {
        this.ngZone.run(() => {
          if (!this.speechListening) return;
          const trimmed = transcript.trim();
          this.speechStatus = trimmed
            ? `Listening… ${trimmed}`
            : 'Listening…';
        });
      },
      onFinal: (transcript) => {
        this.ngZone.run(() => this.applySpeechFinal(transcript));
      },
      onError: (message) => {
        this.ngZone.run(() => {
          this.speechListening = false;
          this.speechStatus = message;
        });
      },
    });
    this.voiceNote = new ComposerVoiceNoteRecorder({
      onRecordingChange: (recording) => {
        this.ngZone.run(() => {
          this.voiceNoteRecording = recording;
          if (recording) {
            this.voiceNoteStatus = `Recording… ${formatVoiceNoteSeconds(0)}`;
          } else if (this.voiceNoteStatus.startsWith('Recording')) {
            this.voiceNoteStatus = '';
          }
        });
      },
      onTick: (seconds) => {
        this.ngZone.run(() => {
          this.voiceNoteSeconds = seconds;
          if (this.voiceNoteRecording) {
            this.voiceNoteStatus = `Recording… ${formatVoiceNoteSeconds(seconds)}`;
          }
        });
      },
      onComplete: (file) => {
        this.ngZone.run(() => this.applyVoiceNoteFile(file));
      },
      onError: (message) => {
        this.ngZone.run(() => {
          this.voiceNoteRecording = false;
          this.voiceNoteStatus = message;
        });
      },
    });
  }

  ngOnInit(): void {
    this.restoreReplyWatch();
    this.navSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.syncVisibility((event as NavigationEnd).urlAfterRedirects);
      });
    this.syncVisibility(this.router.url);
  }

  ngOnDestroy(): void {
    this.stopComposerCapture(true);
    this.navSub?.unsubscribe();
    this.templatesSub?.unsubscribe();
    this.stopThreadActivity();
    this.uploadSub?.unsubscribe();
    this.uploadSub = undefined;
    this.stopWatch();
    this.stopNudge();
    this.conversationsSub?.unsubscribe();
    this.startChatSub?.unsubscribe();
    this.closeChatSub?.unsubscribe();
    this.historySub?.unsubscribe();
  }

  ngAfterViewChecked(): void {
    if (!this.shouldScrollThread) return;
    if (this.view !== 'thread') {
      this.shouldScrollThread = false;
      return;
    }
    const el = this.panelBody?.nativeElement;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    this.shouldScrollThread = false;
  }

  get panelTitle(): string {
    if (this.view === 'thread') return 'Questions? Chat with us.';
    if (this.view === 'history') return 'Chat history';
    if (this.view === 'searches') return 'Searches';
    if (this.view === 'categories') return 'Rio';
    return this.selectedCategory?.title || 'Rio';
  }

  get searchesChipLabel(): string {
    return SEARCHES_CHIP_LABEL;
  }

  get panelSubtitle(): string | null {
    return this.view === 'thread' ? 'Our team can also help' : null;
  }

  get launcherLabel(): string {
    if (this.unreadCount > 0) {
      return `Rio, ${this.unreadCount} new replies`;
    }
    return 'Rio';
  }

  get showThreadChrome(): boolean {
    return this.view === 'thread';
  }

  get showComposer(): boolean {
    if (
      this.view === 'categories' ||
      this.view === 'searches' ||
      this.view === 'questions' ||
      this.view === 'answer'
    ) {
      return true;
    }
    return this.view === 'thread' && this.conversationStatus !== 'closed';
  }

  /** FAQ browse views (home / topic questions / answer) share FAQ-first send. */
  get isFaqBrowseView(): boolean {
    return (
      this.view === 'categories' ||
      this.view === 'searches' ||
      this.view === 'questions' ||
      this.view === 'answer'
    );
  }

  get composerPlaceholder(): string {
    if (this.view === 'categories') {
      return 'What are you looking for?';
    }
    if (
      this.view === 'searches' ||
      this.view === 'questions' ||
      this.view === 'answer'
    ) {
      return 'Ask something else…';
    }
    return 'Compose your message…';
  }

  get canSend(): boolean {
    if (!this.showComposer || this.sending || this.attachUploading) {
      return false;
    }
    if (this.isFaqBrowseView) {
      const hasText = this.draft.trim().length > 0;
      const hasFile = !!this.pendingHomeAttachFile;
      return (
        (hasText || hasFile) &&
        !this.startingChat &&
        this.pendingHomeSend === null
      );
    }
    const hasText = this.draft.trim().length > 0;
    const hasAttachment =
      !!this.pendingAttachment || !!this.pendingHomeAttachFile;
    return !!this.conversationId && (hasText || hasAttachment);
  }

  get showThreadAttach(): boolean {
    return this.view === 'thread' && this.conversationStatus !== 'closed';
  }

  /** Paperclip is active on FAQ home/start and in an open admin thread. */
  get showAttachControl(): boolean {
    return this.isFaqBrowseView || this.showThreadAttach;
  }

  get showAttachChip(): boolean {
    return (
      this.showAttachControl &&
      (!!this.pendingAttachment ||
        !!this.pendingHomeAttachFile ||
        this.attachUploading)
    );
  }

  get pendingAttachDisplayName(): string {
    return (
      this.pendingAttachment?.attachment_name ||
      this.pendingHomeAttachFile?.name ||
      ''
    );
  }

  /** Uppercase extension / short type label for the inline attach card. */
  get pendingAttachTypeLabel(): string {
    const name = this.pendingAttachDisplayName;
    const dot = name.lastIndexOf('.');
    if (dot > 0 && dot < name.length - 1) {
      return name.slice(dot + 1).toUpperCase();
    }
    const mime = (
      this.pendingAttachment?.attachment_mime ||
      this.pendingHomeAttachFile?.type ||
      ''
    ).toLowerCase();
    if (mime.startsWith('image/')) return 'IMAGE';
    if (mime === 'application/pdf') return 'PDF';
    if (mime.startsWith('audio/')) return 'AUDIO';
    return 'FILE';
  }

  get dictateTitle(): string {
    if (!this.speechSupported) {
      return 'Speech input not supported in this browser';
    }
    return this.speechListening ? 'Stop dictation' : 'Dictate message';
  }

  get dictateAriaLabel(): string {
    return this.speechListening ? 'Stop dictation' : 'Dictate message';
  }

  get micTitle(): string {
    if (!this.voiceNoteSupported) {
      return 'Voice notes not supported in this browser';
    }
    return this.voiceNoteRecording ? 'Stop recording' : 'Record voice note';
  }

  get micAriaLabel(): string {
    return this.voiceNoteRecording ? 'Stop recording' : 'Record voice note';
  }

  get composerVoiceStatus(): string {
    return this.voiceNoteStatus || this.speechStatus;
  }

  /** Search product topics already filtered by subscription permissions on the API. */
  get searchTopicCategories(): SupportTemplateCategory[] {
    return this.categories.filter((category) =>
      SEARCH_TOPIC_KEYS.has(category.key),
    );
  }

  get showSearchesChip(): boolean {
    return (
      !this.showingMatchedQuestions &&
      this.matchedCategoryChips.length === 0 &&
      this.searchTopicCategories.length > 0
    );
  }

  get suggestionChips(): SupportTemplateCategory[] {
    if (this.matchedQuestionChips.length > 0) {
      return [];
    }
    const source =
      this.matchedCategoryChips.length > 0
        ? this.matchedCategoryChips
        : this.categories.filter(
            (category) => !SEARCH_TOPIC_KEYS.has(category.key),
          );
    // Keep default chips while typing; longest-first so wrap packs large→small.
    return [...source]
      .slice(0, HOME_CHIP_LIMIT)
      .sort((a, b) => {
        const lenDiff = (b.title?.length ?? 0) - (a.title?.length ?? 0);
        if (lenDiff !== 0) return lenDiff;
        return (a.sort_order ?? 0) - (b.sort_order ?? 0);
      });
  }

  get showingMatchedQuestions(): boolean {
    return this.matchedQuestionChips.length > 0;
  }

  get canCloseChat(): boolean {
    return (
      this.view === 'thread' &&
      this.conversationStatus === 'open' &&
      !!this.conversationId &&
      !this.closingChat
    );
  }

  /** FAQ questions for the selected topic, shortest label first (Crisp-style). */
  get questionItems(): SupportTemplateItem[] {
    const items = this.selectedCategory?.items ?? [];
    // Keep all topic questions visible while typing in the composer.
    return [...items].sort((a, b) => {
      const lenDiff = (a.question?.length ?? 0) - (b.question?.length ?? 0);
      if (lenDiff !== 0) return lenDiff;
      return (a.sort_order ?? 0) - (b.sort_order ?? 0);
    });
  }

  openPanel(): void {
    this.stopNudge();
    if (this.unreadCount > 0) {
      this.openReply();
      return;
    }
    this.open = true;
    this.menuOpen = false;
    this.view = 'categories';
    this.selectedCategory = null;
    this.selectedItem = null;
    this.clearHomeFaqSuggestions();
    this.loadTemplates();
  }

  openReply(): void {
    this.stopNudge();
    this.stopWatch();
    this.acknowledgeReplies = true;
    this.markAdminRepliesSeen(this.messages);
    this.unreadCount = 0;
    this.open = true;
    this.menuOpen = false;
    this.selectedCategory = null;
    this.selectedItem = null;
    this.openCurrentOpenChat();
  }

  closePanel(): void {
    this.stopComposerCapture();
    this.stopThreadActivity();
    this.open = false;
    this.menuOpen = false;
    this.panelExpanded = false;
    this.emojiPickerOpen = false;
    this.loading = false;
    this.view = 'categories';
    this.selectedCategory = null;
    this.selectedItem = null;
    this.clearHomeFaqSuggestions();
    this.clearThreadState();
    this.clearHistoryState();
    this.templatesSub?.unsubscribe();
    this.startWatch();
    this.startNudge();
  }

  /** Quick Actions → Minimize chat (same as closing the panel to the launcher). */
  minimizeChat(): void {
    this.menuOpen = false;
    this.closePanel();
  }

  /** Quick Actions → Expand / Collapse view. */
  toggleExpandedView(): void {
    this.menuOpen = false;
    this.panelExpanded = !this.panelExpanded;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open) return;
    const target = event.target;
    if (target instanceof Node && this.host.nativeElement.contains(target)) {
      if (
        this.emojiPickerOpen &&
        target instanceof Element &&
        !target.closest('.help-bot-emoji-wrap')
      ) {
        this.emojiPickerOpen = false;
      }
      if (
        this.menuOpen &&
        target instanceof Element &&
        !target.closest('.help-bot-menu')
      ) {
        this.menuOpen = false;
      }
      return;
    }
    this.closePanel();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.open) return;
    if (this.emojiPickerOpen) {
      this.emojiPickerOpen = false;
      return;
    }
    if (this.menuOpen) {
      this.menuOpen = false;
      return;
    }
    this.closePanel();
  }

  toggleEmojiPicker(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.menuOpen = false;
    this.emojiPickerOpen = !this.emojiPickerOpen;
  }

  insertEmoji(emoji: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const input = this.composerInput?.nativeElement;
    const start = input?.selectionStart ?? this.draft.length;
    const end = input?.selectionEnd ?? this.draft.length;
    const next = insertTextAtCursor(this.draft, emoji, start, end);
    this.draft = next.value;
    this.emojiPickerOpen = false;
    if (this.sendError) this.sendError = '';
    if (this.view === 'categories') {
      this.clearHomeFaqSuggestions();
    }
    if (input) {
      input.value = next.value;
      input.focus();
      input.setSelectionRange(next.caret, next.caret);
    }
  }

  toggleSpeechDictation(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.speechSupported || this.sending || this.attachUploading) return;
    if (!this.showComposer) return;
    this.emojiPickerOpen = false;
    this.menuOpen = false;
    this.stopVoiceNoteRecording();
    if (this.speechListening) {
      this.speech.stop();
      return;
    }
    this.speechStatus = '';
    this.speech.toggle();
  }

  toggleVoiceNote(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.voiceNoteSupported || this.sending || this.attachUploading) return;
    if (!this.showComposer) return;
    this.emojiPickerOpen = false;
    this.menuOpen = false;
    this.stopSpeechDictation();
    if (this.voiceNoteRecording) {
      this.voiceNote.stop();
      return;
    }
    this.voiceNoteStatus = '';
    void this.voiceNote.toggle();
  }

  back(): void {
    this.menuOpen = false;
    this.emojiPickerOpen = false;
    if (this.view === 'history') {
      this.clearHistoryState();
      if (this.historyReturnView === 'thread' && this.conversationId) {
        this.view = 'thread';
        this.startPolling();
        return;
      }
      this.stopComposerCapture();
      this.stopThreadActivity();
      this.clearThreadState();
      this.view = 'categories';
      this.startWatch();
      return;
    }
    if (this.view === 'thread') {
      this.stopComposerCapture();
      this.stopThreadActivity();
      this.clearThreadState();
      this.view = 'categories';
      this.startWatch();
      return;
    }
    if (this.view === 'answer') {
      this.view = 'questions';
      this.selectedItem = null;
      return;
    }
    if (this.view === 'questions') {
      const fromSearches =
        !!this.selectedCategory &&
        SEARCH_TOPIC_KEYS.has(this.selectedCategory.key);
      this.selectedCategory = null;
      this.selectedItem = null;
      this.view = fromSearches ? 'searches' : 'categories';
      return;
    }
    if (this.view === 'searches') {
      this.view = 'categories';
    }
  }

  openSearchTopics(): void {
    this.menuOpen = false;
    this.stopComposerCapture();
    this.draft = '';
    this.clearHomeFaqSuggestions();
    this.selectedCategory = null;
    this.selectedItem = null;
    this.view = 'searches';
  }

  selectCategory(category: SupportTemplateCategory): void {
    this.menuOpen = false;
    this.stopComposerCapture();
    this.draft = '';
    this.clearHomeFaqSuggestions();
    this.selectedCategory = category;
    this.selectedItem = null;
    this.view = 'questions';
  }

  selectItem(item: SupportTemplateItem): void {
    this.menuOpen = false;
    this.stopComposerCapture();
    this.draft = '';
    this.clearHomeFaqSuggestions();
    this.selectedItem = item;
    this.view = 'answer';
  }

  selectMatchedItem(row: HelpBotFaqMatchedItem): void {
    this.menuOpen = false;
    this.stopComposerCapture();
    this.draft = '';
    this.clearHomeFaqSuggestions();
    this.selectedCategory = row.category;
    this.selectedItem = row.item;
    this.view = 'answer';
  }

  /** From FAQ answer view: open (or resume) admin chat without posting. */
  escalateToAdmin(): void {
    this.menuOpen = false;
    this.clearHomeFaqSuggestions();
    this.openThread();
  }

  openThread(): void {
    this.menuOpen = false;
    this.stopWatch();
    this.selectedCategory = null;
    this.selectedItem = null;
    this.openCurrentOpenChat();
  }

  toggleMenu(event?: Event): void {
    event?.stopPropagation();
    this.emojiPickerOpen = false;
    this.menuOpen = !this.menuOpen;
  }

  startNewChat(): void {
    this.menuOpen = false;
    if (this.startingChat) return;
    this.startingChat = true;
    this.stopThreadActivity();
    const pendingHomeSend = this.pendingHomeSend;
    const pendingHomeAttachFile = this.pendingHomeAttachFile;
    this.clearThreadState();
    this.pendingHomeSend = pendingHomeSend;
    this.pendingHomeAttachFile = pendingHomeAttachFile;
    this.view = 'thread';
    this.threadLoading = true;
    this.threadError = '';
    this.startChatSub?.unsubscribe();
    this.startChatSub = this.supportMessages.startConversation().subscribe({
      next: (conversation) => {
        this.startingChat = false;
        this.conversationId = conversation.conversation_id;
        this.conversationStatus = conversation.status || 'open';
        this.messages = [];
        this.threadLoading = false;
        this.threadError = '';
        this.shouldScrollThread = true;
        this.startPolling();
        this.flushPendingHomeSend();
      },
      error: () => {
        this.startingChat = false;
        this.threadLoading = false;
        this.threadError = 'Could not start a new chat.';
        this.abortPendingHomeSend();
      },
    });
  }

  openHistory(): void {
    this.menuOpen = false;
    this.emojiPickerOpen = false;
    this.stopComposerCapture();
    this.historyReturnView =
      this.view === 'thread' || this.view === 'history'
        ? this.view === 'history'
          ? this.historyReturnView
          : 'thread'
        : 'categories';
    if (this.view === 'thread') {
      this.pollSub?.unsubscribe();
      this.pollSub = undefined;
      this.stopComposerCapture();
    }
    this.view = 'history';
    this.loadHistory();
  }

  selectHistoryItem(item: SupportConversationSummary): void {
    this.menuOpen = false;
    this.stopWatch();
    this.clearHistoryState();
    this.clearThreadState();
    this.conversationId = item.conversation_id;
    this.conversationStatus = item.status;
    this.view = 'thread';
    this.loadMessages();
    this.startPolling();
  }

  closeChat(): void {
    this.menuOpen = false;
    if (!this.canCloseChat || !this.conversationId) return;
    const id = this.conversationId;
    this.closingChat = true;
    this.closeChatSub?.unsubscribe();
    this.closeChatSub = this.supportMessages.closeConversation(id).subscribe({
      next: () => {
        this.closingChat = false;
        this.stopThreadActivity();
        this.clearThreadState();
        this.view = 'categories';
        this.selectedCategory = null;
        this.selectedItem = null;
        this.startWatch();
      },
      error: () => {
        this.closingChat = false;
        this.sendError = 'Chat could not be closed.';
      },
    });
  }

  senderLabel(sender: string): string {
    const role = (sender || '').toLowerCase();
    if (role === 'admin') return 'Admin';
    if (role === 'ai') return 'Assistant';
    return '';
  }

  isOwnMessage(sender: string): boolean {
    return (sender || '').toLowerCase() === 'user';
  }

  get threadRows(): HelpBotThreadRow[] {
    return buildThreadRows(this.messages);
  }

  trackThreadRow(_index: number, row: HelpBotThreadRow): string {
    return row.id;
  }

  trackMessage(_index: number, message: SupportChatMessage): string {
    return supportMessageKey(message);
  }

  trackHistory(_index: number, item: SupportConversationSummary): string {
    return item.conversation_id;
  }

  historyLabel(item: SupportConversationSummary): string {
    const preview = (item.preview || '').trim();
    return preview || 'Chat';
  }

  historyWhen(item: SupportConversationSummary): string {
    return this.formatHistoryDate(item.last_message_at || item.created_on);
  }

  onDraftInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.draft = target?.value ?? '';
    if (this.sendError) this.sendError = '';
    // Clear post-send FAQ match chips once the user edits the draft again.
    if (this.view === 'categories') {
      this.clearHomeFaqSuggestions();
    }
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    this.sendMessage();
  }

  openAttachPicker(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.showAttachControl || this.sending || this.attachUploading) return;
    this.attachError = '';
    this.attachInput?.nativeElement?.click();
  }

  onAttachSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file || !this.showAttachControl) return;

    if (file.size > SUPPORT_ATTACH_MAX_BYTES) {
      this.attachError = 'File must be 5MB or smaller.';
      return;
    }
    if (!isAllowedSupportAttachMime(file.type)) {
      this.attachError =
        'Only JPEG, PNG, WebP, GIF, PDF, or audio files are allowed.';
      return;
    }

    this.stageAttachmentFile(file);
  }

  clearPendingAttachment(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.pendingAttachment = null;
    this.pendingHomeAttachFile = null;
    this.attachError = '';
  }

  isImageAttachment(message: SupportChatMessage): boolean {
    const mime = (message.attachment_mime || '').toLowerCase();
    return mime.startsWith('image/') && !!message.attachment_url;
  }

  isAudioAttachment(message: SupportChatMessage): boolean {
    if (!message.attachment_url) return false;
    const mime = (message.attachment_mime || '').toLowerCase();
    if (mime.startsWith('audio/')) return true;
    const name = (
      message.attachment_name ||
      attachmentPath(message.attachment_url)
    ).toLowerCase();
    return /\.(webm|ogg|mp3|m4a|wav|mpeg)(?:$|\?)/i.test(name);
  }

  sendMessage(event?: Event): void {
    event?.preventDefault();
    if (this.isFaqBrowseView) {
      this.sendFromHome();
      return;
    }
    const body = this.draft.trim();
    const attachment = this.pendingAttachment;
    const homeFile = this.pendingHomeAttachFile;
    if (
      (!body && !attachment && !homeFile) ||
      this.sending ||
      this.attachUploading ||
      this.view !== 'thread' ||
      this.conversationStatus === 'closed' ||
      !this.conversationId
    ) {
      return;
    }

    // Home file still pending (e.g. after a failed auto-upload) — upload then send.
    if (homeFile && !attachment) {
      const conversationId = this.conversationId;
      this.attachUploading = true;
      this.attachError = '';
      this.uploadSub?.unsubscribe();
      this.uploadSub = this.supportMessages
        .uploadConversationAttachment(conversationId, homeFile)
        .subscribe({
          next: (meta) => {
            if (this.conversationId !== conversationId) return;
            this.attachUploading = false;
            this.pendingHomeAttachFile = null;
            this.pendingAttachment = meta;
            this.sendMessage();
          },
          error: () => {
            if (this.conversationId !== conversationId) return;
            this.attachUploading = false;
            this.attachError = 'Attachment could not be uploaded.';
          },
        });
      return;
    }

    const conversationId = this.conversationId;
    this.stopComposerCapture();
    this.sending = true;
    this.sendError = '';
    this.attachError = '';
    this.sendSub?.unsubscribe();
    this.sendSub = this.supportMessages
      .sendConversationMessage(conversationId, body, attachment)
      .subscribe({
        next: (message) => {
          this.sending = false;
          this.draft = '';
          this.pendingAttachment = null;
          this.pendingHomeAttachFile = null;
          this.rememberSent(message);
        },
        error: () => {
          this.sending = false;
          this.sendError = 'Message could not be sent.';
        },
      });
  }

  /**
   * Home Send: FAQ-match first; escalate to admin only when there is no useful match.
   * A pending home attachment always escalates (files go to the admin thread).
   */
  private sendFromHome(): void {
    const body = this.draft.trim();
    const homeFile = this.pendingHomeAttachFile;
    if (
      (!body && !homeFile) ||
      this.sending ||
      this.startingChat ||
      this.pendingHomeSend !== null ||
      this.attachUploading
    ) {
      return;
    }

    this.stopComposerCapture();

    if (homeFile) {
      this.clearHomeFaqSuggestions();
      this.pendingHomeSend = body;
      this.draft = '';
      this.sendError = '';
      this.openThread();
      return;
    }

    const match = matchHelpBotFaq(body, this.categories);
    if (match.type === 'answer') {
      this.draft = '';
      this.sendError = '';
      this.clearHomeFaqSuggestions();
      this.selectedCategory = match.category;
      this.selectedItem = match.item;
      this.view = 'answer';
      return;
    }

    if (match.type === 'suggestions') {
      this.sendError = '';
      // Clear draft so matched chips stay visible (live filter would hide them).
      this.draft = '';
      this.matchedQuestionChips = match.items?.length ? [...match.items] : [];
      this.matchedCategoryChips =
        !match.items?.length && match.categories?.length
          ? [...match.categories]
          : [];
      this.view = 'categories';
      return;
    }

    this.clearHomeFaqSuggestions();
    this.pendingHomeSend = body;
    this.draft = '';
    this.sendError = '';
    this.openThread();
  }

  private clearHomeFaqSuggestions(): void {
    this.matchedQuestionChips = [];
    this.matchedCategoryChips = [];
  }

  private flushPendingHomeSend(): void {
    if (
      this.pendingHomeSend === null ||
      this.view !== 'thread' ||
      this.conversationStatus === 'closed' ||
      !this.conversationId ||
      this.sending ||
      this.attachUploading
    ) {
      return;
    }
    const body = this.pendingHomeSend;
    const file = this.pendingHomeAttachFile;
    const conversationId = this.conversationId;

    if (file) {
      this.attachUploading = true;
      this.attachError = '';
      this.uploadSub?.unsubscribe();
      this.uploadSub = this.supportMessages
        .uploadConversationAttachment(conversationId, file)
        .subscribe({
          next: (attachment) => {
            if (this.conversationId !== conversationId) return;
            this.attachUploading = false;
            this.pendingHomeAttachFile = null;
            this.pendingAttachment = attachment;
            this.pendingHomeSend = null;
            this.draft = body;
            this.sendMessage();
          },
          error: () => {
            if (this.conversationId !== conversationId) return;
            this.attachUploading = false;
            this.attachError = 'Attachment could not be uploaded.';
            this.abortPendingHomeSend();
          },
        });
      return;
    }

    this.pendingHomeSend = null;
    this.draft = body;
    this.sendMessage();
  }

  private abortPendingHomeSend(): void {
    if (this.pendingHomeSend === null && !this.pendingHomeAttachFile) return;
    if (this.pendingHomeSend !== null) {
      this.draft = this.pendingHomeSend;
    }
    this.pendingHomeSend = null;
    // Keep pendingHomeAttachFile so the user can retry from home after Back.
  }

  private uploadAttachmentForConversation(
    conversationId: string,
    file: File,
  ): void {
    this.attachUploading = true;
    this.attachError = '';
    this.uploadSub?.unsubscribe();
    this.uploadSub = this.supportMessages
      .uploadConversationAttachment(conversationId, file)
      .subscribe({
        next: (attachment) => {
          if (this.conversationId !== conversationId) return;
          this.attachUploading = false;
          this.pendingAttachment = attachment;
          this.pendingHomeAttachFile = null;
        },
        error: () => {
          if (this.conversationId !== conversationId) return;
          this.attachUploading = false;
          this.pendingAttachment = null;
          this.attachError = 'Attachment could not be uploaded.';
        },
      });
  }

  loadMessages(): void {
    this.messagesSub?.unsubscribe();
    const conversationId = this.conversationId;
    if (!conversationId) {
      this.threadLoading = false;
      this.threadError = 'Messages could not be loaded.';
      return;
    }
    const showLoading =
      this.messages.length === 0 && this.pendingLocal.length === 0;
    if (showLoading) {
      this.threadLoading = true;
      this.threadError = '';
    }
    this.messagesSub = this.supportMessages
      .getConversationMessages(conversationId)
      .subscribe({
        next: (thread) => {
          if (this.conversationId !== conversationId) return;
          this.conversationId = thread.conversation_id || conversationId;
          this.conversationStatus =
            thread.status ?? this.conversationStatus ?? 'open';
          if (this.conversationStatus === 'closed') {
            this.stopComposerCapture();
          }
          this.applyServerMessages(thread.messages);
          this.threadLoading = false;
          this.threadError = '';
          this.flushPendingHomeSend();
        },
        error: () => {
          if (this.conversationId !== conversationId) return;
          this.threadLoading = false;
          if (this.messages.length === 0) {
            this.threadError = 'Messages could not be loaded.';
            this.abortPendingHomeSend();
          }
        },
      });
  }

  pageRoute(item: SupportTemplateItem | null): string | null {
    const route = item?.route?.trim() ?? '';
    if (!route || /^https?:/i.test(route) || route.startsWith('//')) {
      return null;
    }
    return route.startsWith('/') ? route : `/${route}`;
  }

  loadTemplates(): void {
    this.templatesSub?.unsubscribe();
    this.loading = true;
    this.error = '';
    this.templatesSub = this.supportTemplates.getTemplates().subscribe({
      next: (categories) => {
        this.categories = prepareHelpBotCategories(categories);
        this.loading = false;
      },
      error: () => {
        this.categories = [];
        this.loading = false;
        this.view = 'categories';
        this.selectedCategory = null;
        this.selectedItem = null;
        this.error = 'Rio could not load topics.';
      },
    });
  }

  loadHistory(): void {
    this.historySub?.unsubscribe();
    this.historyLoading = true;
    this.historyError = '';
    this.historySub = this.supportMessages.listConversations().subscribe({
      next: (conversations) => {
        this.historyItems = conversations;
        this.historyLoading = false;
        this.historyError = '';
      },
      error: () => {
        this.historyItems = [];
        this.historyLoading = false;
        this.historyError = 'Chat history could not be loaded.';
      },
    });
  }

  private openCurrentOpenChat(): void {
    const pendingHomeSend = this.pendingHomeSend;
    const pendingHomeAttachFile = this.pendingHomeAttachFile;
    this.clearThreadState();
    this.pendingHomeSend = pendingHomeSend;
    this.pendingHomeAttachFile = pendingHomeAttachFile;
    this.view = 'thread';
    this.threadLoading = true;
    this.threadError = '';
    this.conversationsSub?.unsubscribe();
    this.conversationsSub = this.supportMessages.listConversations().subscribe({
      next: (conversations) => {
        const openChat = conversations.find((row) => row.status === 'open');
        if (openChat) {
          this.conversationId = openChat.conversation_id;
          this.conversationStatus = 'open';
          this.loadMessages();
          this.startPolling();
          return;
        }
        this.startNewChat();
      },
      error: () => {
        // Fall back to legacy open-thread read, then create if empty.
        this.messagesSub?.unsubscribe();
        this.messagesSub = this.supportMessages.getThread().subscribe({
          next: (thread) => {
            if (thread.conversation_id) {
              this.conversationId = thread.conversation_id;
              this.conversationStatus = thread.status ?? 'open';
              this.applyServerMessages(thread.messages);
              this.threadLoading = false;
              this.threadError = '';
              this.startPolling();
              this.flushPendingHomeSend();
              return;
            }
            this.startNewChat();
          },
          error: () => {
            this.threadLoading = false;
            this.threadError = 'Messages could not be loaded.';
            this.abortPendingHomeSend();
          },
        });
      },
    });
  }

  private formatHistoryDate(value: string | null | undefined): string {
    if (!value) return '';
    const time = Date.parse(value);
    if (Number.isNaN(time)) return '';
    try {
      return new Date(time).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  }

  private syncVisibility(url: string): void {
    const next = this.isAnalystPage(url);
    const launcherAppeared = next && !this.visible;
    this.visible = next;
    if (!next) {
      this.closePanel();
      this.stopWatch();
      return;
    }
    this.startWatch();
    if (launcherAppeared) this.startNudge();
  }

  private startNudge(): void {
    this.stopNudge();
    if (!this.visible || this.open) return;
    this.nudgeSub = timer(NUDGE_DELAY_MS, NUDGE_REPEAT_MS).subscribe(() => {
      if (!this.visible || this.open) return;
      this.nudgeVisible = true;
      this.nudgeHideSub?.unsubscribe();
      this.nudgeHideSub = timer(NUDGE_VISIBLE_MS).subscribe(() => {
        this.nudgeVisible = false;
      });
    });
  }

  private stopNudge(): void {
    this.nudgeSub?.unsubscribe();
    this.nudgeHideSub?.unsubscribe();
    this.nudgeSub = undefined;
    this.nudgeHideSub = undefined;
    this.nudgeVisible = false;
  }

  private isAnalystPage(url: string): boolean {
    const path =
      (url || '/').split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
    return this.authService.isAuthenticated() && !PUBLIC_PATHS.has(path);
  }

  private startPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = interval(THREAD_POLL_MS).subscribe(() => {
      if (this.open && this.view === 'thread' && this.conversationId) {
        this.loadMessages();
      }
    });
  }

  private stopComposerCapture(dispose = false): void {
    this.stopSpeechDictation(dispose);
    this.stopVoiceNoteRecording(dispose);
  }

  private stopSpeechDictation(dispose = false): void {
    if (dispose) {
      this.speech.dispose();
    } else {
      this.speech.stop();
    }
    this.speechListening = false;
    if (this.speechStatus.startsWith('Listening')) {
      this.speechStatus = '';
    }
  }

  private applySpeechFinal(transcript: string): void {
    const input = this.composerInput?.nativeElement;
    const start = input?.selectionStart ?? this.draft.length;
    const end = input?.selectionEnd ?? this.draft.length;
    const insert = prepareSpeechTranscriptForInsert(this.draft, transcript, start);
    if (!insert) return;
    const next = insertTextAtCursor(this.draft, insert, start, end);
    this.draft = next.value;
    if (this.sendError) this.sendError = '';
    if (this.view === 'categories') {
      this.clearHomeFaqSuggestions();
    }
    if (input) {
      input.value = next.value;
      input.focus();
      input.setSelectionRange(next.caret, next.caret);
    }
  }

  private stopVoiceNoteRecording(dispose = false): void {
    if (dispose) {
      this.voiceNote.dispose();
    } else {
      this.voiceNote.stop();
    }
    this.voiceNoteRecording = false;
    this.voiceNoteSeconds = 0;
    if (this.voiceNoteStatus.startsWith('Recording')) {
      this.voiceNoteStatus = '';
    }
  }

  private applyVoiceNoteFile(file: File): void {
    if (file.size > SUPPORT_ATTACH_MAX_BYTES) {
      this.attachError = `Voice note must be ${VOICE_NOTE_MAX_SECONDS}s or under (5MB max).`;
      this.voiceNoteStatus = '';
      return;
    }
    this.voiceNoteStatus = '';
    this.attachError = '';
    this.stageAttachmentFile(file);
  }

  private stageAttachmentFile(file: File): void {
    if (this.isFaqBrowseView || !this.conversationId) {
      this.pendingHomeAttachFile = file;
      this.pendingAttachment = null;
      this.attachError = '';
      return;
    }
    this.uploadAttachmentForConversation(this.conversationId, file);
  }

  private stopThreadActivity(): void {
    this.stopComposerCapture();
    this.pollSub?.unsubscribe();
    this.messagesSub?.unsubscribe();
    this.sendSub?.unsubscribe();
    this.startChatSub?.unsubscribe();
    this.closeChatSub?.unsubscribe();
    this.conversationsSub?.unsubscribe();
    this.pollSub = undefined;
    this.messagesSub = undefined;
    this.sendSub = undefined;
    this.startChatSub = undefined;
    this.closeChatSub = undefined;
    this.conversationsSub = undefined;
    this.threadLoading = false;
    this.sending = false;
    this.startingChat = false;
    this.closingChat = false;
    this.shouldScrollThread = false;
  }

  private clearThreadState(): void {
    this.messages = [];
    this.pendingLocal = [];
    this.draft = '';
    this.pendingAttachment = null;
    this.pendingHomeAttachFile = null;
    this.attachUploading = false;
    this.attachError = '';
    this.uploadSub?.unsubscribe();
    this.uploadSub = undefined;
    this.pendingHomeSend = null;
    this.threadError = '';
    this.sendError = '';
    this.conversationId = null;
    this.conversationStatus = null;
  }

  private clearHistoryState(): void {
    this.historyLoading = false;
    this.historyError = '';
    this.historyItems = [];
    this.historySub?.unsubscribe();
    this.historySub = undefined;
  }

  private applyServerMessages(serverMessages: SupportChatMessage[]): void {
    const raw = Array.isArray(serverMessages) ? serverMessages : [];
    const server = reuseAttachmentUrls(this.messages, raw);
    const seen = new Set(server.map((message) => supportMessageKey(message)));
    this.pendingLocal = this.pendingLocal.filter(
      (message) => !seen.has(supportMessageKey(message)),
    );
    const next = [...server, ...this.pendingLocal];
    const prevKeys = this.messages.map((message) => supportMessageKey(message));
    const nextKeys = next.map((message) => supportMessageKey(message));
    const changed =
      prevKeys.length !== nextKeys.length ||
      prevKeys.some((key, index) => key !== nextKeys[index]);
    if (changed) {
      this.messages = next;
      this.shouldScrollThread = true;
    }
    if (this.acknowledgeReplies) {
      this.markAdminRepliesSeen(server);
      this.acknowledgeReplies = false;
      this.unreadCount = 0;
      return;
    }
    this.noteUnread(server);
  }

  private rememberSent(message: SupportChatMessage): void {
    const key = supportMessageKey(message);
    if (!this.pendingLocal.some((item) => supportMessageKey(item) === key)) {
      this.pendingLocal = [...this.pendingLocal, message];
    }
    if (!this.messages.some((item) => supportMessageKey(item) === key)) {
      this.messages = [...this.messages, message];
    }
    this.shouldScrollThread = true;
    if (!this.waitingSince) {
      this.waitingSince = message.created_on || new Date().toISOString();
      this.persistWaitingSince();
    }
  }

  private restoreReplyWatch(): void {
    try {
      const seen = JSON.parse(sessionStorage.getItem(SEEN_ADMIN_STORAGE_KEY) || '[]');
      if (Array.isArray(seen)) {
        this.seenAdminKeys = new Set(
          seen.filter((key) => typeof key === 'string'),
        );
      }
      this.waitingSince = sessionStorage.getItem(WAITING_SINCE_STORAGE_KEY) || '';
    } catch {
      this.seenAdminKeys = new Set();
      this.waitingSince = '';
    }
  }

  private persistWaitingSince(): void {
    try {
      sessionStorage.setItem(WAITING_SINCE_STORAGE_KEY, this.waitingSince);
    } catch {
      // Unread replies still show on the launcher for this page load.
    }
  }

  private persistSeen(): void {
    try {
      sessionStorage.setItem(
        SEEN_ADMIN_STORAGE_KEY,
        JSON.stringify([...this.seenAdminKeys]),
      );
    } catch {
      // Unread replies still show on the launcher for this page load.
    }
  }

  private isAdminReply(message: SupportChatMessage): boolean {
    return (message.sender || '').toLowerCase() === 'admin';
  }

  private messageTime(value: string): number {
    const time = Date.parse(value || '');
    return Number.isNaN(time) ? 0 : time;
  }

  private isUnseenAdminReply(message: SupportChatMessage): boolean {
    if (!this.waitingSince || !this.isAdminReply(message)) return false;
    if (this.seenAdminKeys.has(supportMessageKey(message))) return false;
    const created = this.messageTime(message.created_on);
    const since = this.messageTime(this.waitingSince);
    if (!created || !since) return true;
    return created >= since;
  }

  private markAdminRepliesSeen(messages: SupportChatMessage[]): void {
    for (const message of messages) {
      if (this.isAdminReply(message)) {
        this.seenAdminKeys.add(supportMessageKey(message));
      }
    }
    this.persistSeen();
  }

  private noteUnread(serverMessages: SupportChatMessage[]): void {
    this.unreadCount = (serverMessages || []).filter((message) =>
      this.isUnseenAdminReply(message),
    ).length;
  }

  private startWatch(): void {
    if (!this.visible || !this.waitingSince || this.watchSub) return;
    if (this.open && this.view === 'thread') return;
    this.watchSub = timer(0, THREAD_POLL_MS).subscribe(() => {
      if (!this.visible || !this.waitingSince) return;
      if (this.open && this.view === 'thread') return;
      this.watchMessagesSub?.unsubscribe();
      this.watchMessagesSub = this.supportMessages.getThread().subscribe({
        next: (thread) => this.noteUnread(thread.messages),
        error: () => undefined,
      });
    });
  }

  private stopWatch(): void {
    this.watchSub?.unsubscribe();
    this.watchMessagesSub?.unsubscribe();
    this.watchSub = undefined;
    this.watchMessagesSub = undefined;
  }
}
